import * as React from 'react';
import { cn } from '@/lib/utils/cn';

/**
 * AiSurface — polymorphic wrapper that visually marks AI-derived content.
 *
 * Why this is its own primitive:
 *   - Per README §6 + the AI-surface section of the design handoff, AI
 *     content is *never* styled as a regular row. The visual separation —
 *     soft lavender background, ink-purple text, lavender hairline — is
 *     part of the auditability story: a user can always tell at a glance
 *     which numbers / claims came from the model and which came from
 *     consultants or contract data.
 *   - We compose this wrapper everywhere AI surfaces appear: register-row
 *     AI proposals (T-0045), AI chat messages (T-0053), AI synthesis
 *     sections (T-0077), AI exec-summary drafts (T-0089). Building it once
 *     here means a future re-skin only touches this file.
 *
 * Polymorphic API:
 *   - Pass `as="tr"` for table rows, `as="div"` for cards / message
 *     bubbles. The default is `div`. Props for the chosen element flow
 *     through (className, onClick, role, etc.) via
 *     `ComponentPropsWithoutRef<T>`.
 *   - Refs are intentionally NOT forwarded for v1 — none of the
 *     near-term consumers need them. We can add `forwardRef<...>` later
 *     if a focus / scroll consumer materializes.
 *
 * Tokens (from `design_handoff_caliber_v2/prototype/tokens.css`,
 * surfaced as Tailwind utilities by T-0090):
 *   - bg          → `bg-ai-surface`           (#EFF0F8)
 *   - border      → `border-ai-surface-border` (#C9CEE2)
 *   - text/ink    → `text-ai-ink`             (#3D4775)
 *
 * The `<tr>` variant mirrors the prototype's `.tbl tr.ai-row` rule —
 * lavender background + a left inset shadow that reads as a hairline
 * even though `<tr>` cannot carry borders directly. The `<div>` variant
 * uses a proper left border for the same visual contract.
 */
type AsProp<T extends React.ElementType> = { as?: T };

export type AiSurfaceProps<T extends React.ElementType = 'div'> = AsProp<T> &
  Omit<React.ComponentPropsWithoutRef<T>, 'as'> & {
    children?: React.ReactNode;
    className?: string;
  };

/**
 * Class-list builder. Returns the right token wrapping for the chosen
 * element kind:
 *   - tr  : lavender row treatment + inset left rail (borders on `tr` are
 *           unreliable across browsers, so we use `box-shadow: inset`).
 *   - any : surface bg + lavender hairline border (default for div / li /
 *           article / etc.).
 */
function aiSurfaceClasses(as: React.ElementType, extra?: string): string {
  if (as === 'tr') {
    return cn(
      // Lavender row treatment, ink-purple text, ai-surface-border rail
      // pinned to the leading edge via inset shadow. 2px to match the
      // prototype's `.tbl tr.ai-row td` rule.
      'bg-ai-surface text-ai-ink shadow-[inset_2px_0_0_var(--ai-surface-border)]',
      extra,
    );
  }
  return cn('bg-ai-surface text-ai-ink border border-ai-surface-border', extra);
}

export function AiSurface<T extends React.ElementType = 'div'>({
  as,
  className,
  children,
  ...rest
}: AiSurfaceProps<T>) {
  const Comp = (as ?? 'div') as React.ElementType;
  return (
    <Comp className={aiSurfaceClasses(Comp, className)} {...rest}>
      {children}
    </Comp>
  );
}

AiSurface.displayName = 'AiSurface';
